import * as React from 'react';
import { Button, Card, CardBody, CardHeader, Col, Container, Label, Navbar, NavbarBrand, Row } from 'reactstrap';
import { FaChevronDown, FaChevronUp, FaPrint } from 'react-icons/fa';
import { useContext, useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import { authAxios } from '../../services/AxiosService';
import { IHoursRecord } from '../Files/FileContext';
import { DomainContext, ISupportWorkerType } from '../../contexts/DomainContext';
import { formatDate, formatDateForServer } from '../../formatters/DateTimeFormatter';
import DatePicker from 'react-datepicker';
import { Column, TableInstance, usePagination, useSortBy, useTable } from 'react-table';
import { useDefaultColumn } from 'src/hooks/ReactTableHooks';

export interface IActivityTotals {
  timeTrackingActivityId: string;
  hours: number;
}

export const SupportWorkerWorkloadByType = () => {
  const [selectedSupportWorkerTypeId, setSelectedSupportWorkerTypeId] = useState();
  const domainContext = useContext(DomainContext);
  const timeTrackingActivities = domainContext.timeTrackingActivities.filter((tta) => {
    return tta.userRole === 'SupportWorker';
  });
  const [userTimeTracking, setUserTimeTracking] = useState<IHoursRecord[]>([]);
  const [interventionTimeTracking, setInterventionTimeTracking] = useState<IHoursRecord[]>([]);
  const [combinedEntries, setCombinedEntries] = useState<IHoursRecord[]>([]);
  const [activityTotals, setActivityTotals] = useState<IActivityTotals[]>([]);
  const defaultFromDate = new Date();
  const [fromDate, setFromDate] = useState<Date>(
    new Date(defaultFromDate.getMonth() < 3 ? defaultFromDate.getFullYear() - 1 : defaultFromDate.getFullYear(), 3, 1)
  );
  const defaultToDate = new Date();
  defaultToDate.setUTCHours(23, 59, 59, 999);
  const [toDate, setToDate] = useState<Date>(defaultToDate);

  const supportWorkerTypes = useMemo(() => {
    const allSupportWorkerType: ISupportWorkerType = { supportWorkerTypeId: 'all', name: 'All' };
    return [...domainContext.supportWorkerTypes, allSupportWorkerType];
  }, [domainContext.supportWorkerTypes]);

  const selectedSupportWorkerType = useMemo(
    () => supportWorkerTypes.find((u: ISupportWorkerType) => u.supportWorkerTypeId === selectedSupportWorkerTypeId),
    [supportWorkerTypes, selectedSupportWorkerTypeId]
  );

  useEffect(() => {
    if (selectedSupportWorkerType && fromDate && toDate) {
      if (selectedSupportWorkerType.supportWorkerTypeId === 'all') {
        authAxios
          .get(
            `/api/users/admin/support-worker-hours?startDate=${formatDateForServer(
              fromDate
            )}&endDate=${formatDateForServer(toDate)}`
          )
          .then((response) => {
            setUserTimeTracking(response.data);
          });
        authAxios
          .get(
            `/api/support-worker-requests/support-worker-hours?startDate=${formatDateForServer(
              fromDate
            )}&endDate=${formatDateForServer(toDate)}`
          )
          .then((response) => {
            setInterventionTimeTracking(response.data);
          });
      } else {
        authAxios
          .get(
            `/api/users/admin/${
              selectedSupportWorkerType.supportWorkerTypeId
            }/support-worker-hours-by-type?startDate=${formatDateForServer(fromDate)}&endDate=${formatDateForServer(
              toDate
            )}`
          )
          .then((response) => {
            setUserTimeTracking(response.data);
          });
        authAxios
          .get(
            `/api/support-worker-requests/support-worker-hours-by-type/${
              selectedSupportWorkerType.supportWorkerTypeId
            }?startDate=${formatDateForServer(fromDate)}&endDate=${formatDateForServer(toDate)}`
          )
          .then((response) => {
            setInterventionTimeTracking(response.data);
          });
      }
    } else {
      setInterventionTimeTracking([]);
      setUserTimeTracking([]);
    }
  }, [selectedSupportWorkerType, toDate, fromDate]);

  useEffect(() => {
    setCombinedEntries([...userTimeTracking, ...interventionTimeTracking]);
  }, [userTimeTracking, interventionTimeTracking]);

  useEffect(() => {
    const mapTemp = new Map<string, number>();
    combinedEntries.forEach((e) => {
      const totalSoFar = mapTemp.get(e.timeTrackingActivityId);
      const updatedTotal: number = (totalSoFar ? Number(totalSoFar.toFixed(2)) : 0.0) + e.hours;
      mapTemp.set(e.timeTrackingActivityId, Number(updatedTotal.toFixed(2)));
    });
    setActivityTotals(Array.from(mapTemp, ([key, value]) => ({ timeTrackingActivityId: key, hours: value })));
  }, [combinedEntries]);

  const totalDailyValuesFromMap = () => {
    let total = 0;
    activityTotals.forEach((value) => {
      total = total + value.hours;
    });
    return total.toFixed(2);
  };

  const columns: Column<IActivityTotals>[] = useMemo(
    () => [
      {
        Header: 'Activity',
        id: 'timeTrackingActivityId',
        accessor: (record: IActivityTotals) =>
          timeTrackingActivities.find((t) => t.timeTrackingActivityId === record.timeTrackingActivityId)?.name,
        width: 150,
        minWidth: 150,
      },
      {
        Header: 'Hours',
        id: 'hours',
        accessor: (cell: IActivityTotals) => {
          return Number(cell.hours.toFixed(2));
        },
        width: 50,
        minWidth: 50,
      },
    ],
    [combinedEntries, activityTotals]
  );

  const data: IActivityTotals[] = useMemo(() => activityTotals, [activityTotals]);

  const defaultColumn = useDefaultColumn();

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,

    prepareRow,
    page,
    setPageSize,
  }: TableInstance<IActivityTotals> = useTable(
    {
      columns,
      data,
      defaultColumn,
      initialState: {
        sortBy: [
          {
            id: 'timeTrackingActivityId',
            desc: false,
          },
        ],
      },
    },
    useSortBy,
    usePagination
  );

  //set page size to number of entries so they can all be printed at once
  useEffect(() => {
    if (data.length > 0) {
      setPageSize(data.length);
    } else {
      setPageSize(1);
    }
  }, [data, setPageSize]);

  return (
    <div>
      <Navbar color={'light'} light={true} expand={'xs'} className={'border-bottom sticky-top'}>
        <Row className={'flex-fill'}>
          <Col md={3} xl={2}>
            <NavbarBrand>Hours Recorded By Support Worker Type</NavbarBrand>
          </Col>
          <Col className={'d-flex'}>
            <Button className={'ml-auto'} color={'warning'} onClick={() => window.print()}>
              Print <FaPrint />
            </Button>
          </Col>
        </Row>
      </Navbar>
      <Container className={'my-5 container--no-margin-print'}>
        <Row>
          <Col>
            <Card className={'no-print'}>
              <CardHeader>Select Date Range</CardHeader>
              <CardBody>
                <Row>
                  <Col>
                    <Label className={'flex second-label'} style={{ paddingRight: '10px' }}>
                      From
                    </Label>
                    <DatePicker
                      className={'form-control date-select flex'}
                      selected={fromDate}
                      onChange={(date) => {
                        if (date) {
                          setFromDate(date);
                        }
                      }}
                      showMonthDropdown={true}
                      showYearDropdown={true}
                      shouldCloseOnSelect={true}
                      dateFormat={'yyyy-MM-dd'}
                    />
                  </Col>
                  <Col>
                    <Label
                      className={'flex second-label'}
                      for={'EndDate'}
                      style={{ paddingRight: '10px', paddingLeft: '10px' }}
                    >
                      To
                    </Label>
                    <DatePicker
                      className={'form-control date-select flex'}
                      selected={toDate}
                      onChange={(date) => {
                        if (date) {
                          setToDate(date);
                        }
                      }}
                      showMonthDropdown={true}
                      showYearDropdown={true}
                      shouldCloseOnSelect={true}
                      dateFormat={'yyyy-MM-dd'}
                    />
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
          <Col>
            <Card className={'no-print'}>
              <CardHeader>Select Support Worker Type</CardHeader>
              <CardBody>
                <div className={'flex align-items-center'}>
                  <Label className={'flex second-label'} style={{ paddingRight: '13px' }}>
                    Support Worker Type
                  </Label>
                  <Select
                    style={{ maxWidth: '240px' }}
                    options={supportWorkerTypes}
                    placeholder={'Select support worker type...'}
                    value={selectedSupportWorkerType}
                    onChange={(value: any) => {
                      setSelectedSupportWorkerTypeId(value?.supportWorkerTypeId || '');
                    }}
                    getOptionValue={(option: ISupportWorkerType) => option.supportWorkerTypeId}
                    getOptionLabel={(option: ISupportWorkerType) => option.name}
                    isClearable={true}
                  />
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
        <div className={'table-responsive'}>
          <div className={'report mt-5'}>
            <header className={'report-header'}>
              <h2 id={'count-header'} className={'text-center'}>{`Hours Recorded By Support Worker Type (${formatDate(
                new Date()
              )})`}</h2>
              {selectedSupportWorkerType && (
                <h3 className={'text-center h5 text-muted'}>
                  {'Total Time Recorded Between ' +
                    formatDate(fromDate) +
                    ' and ' +
                    formatDate(toDate) +
                    ' for ' +
                    (selectedSupportWorkerType.supportWorkerTypeId === 'all'
                      ? 'All Support Workers'
                      : selectedSupportWorkerType.name + 's') +
                    ': ' +
                    totalDailyValuesFromMap() +
                    ' hours'}
                </h3>
              )}
            </header>
          </div>
          <table className={'table table-bordered report1'} {...getTableProps()} style={{ tableLayout: 'fixed' }}>
            <thead style={{ cursor: 'pointer' }}>
              {headerGroups.map((headerGroup: any, index: number) => (
                <tr key={`users-table-thead-tr-${index}`} {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column: any) => (
                    <th
                      key={`users-table-thead-tr-${index}-${column.id}`}
                      {...column.getHeaderProps({
                        ...column.getSortByToggleProps(),
                        style: {
                          minWidth: column.minWidth,
                          width: column.width,
                          maxWidth: column.maxWidth,
                        },
                      })}
                    >
                      {column.render('Header')}
                      <span>{column.isSorted && (column.isSortedDesc ? <FaChevronDown /> : <FaChevronUp />)}</span>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map((row: any) => {
                prepareRow(row);

                return (
                  <tr key={`users-table-tr-${row.id}`} {...row.getRowProps()}>
                    {row.cells.map((cell: any) => {
                      return (
                        <td
                          key={`users-table-td-${cell.row.id}-${cell.column.id}`}
                          {...cell.getCellProps()}
                          style={{ wordWrap: 'break-word' }}
                        >
                          {cell.render('Cell')}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Container>
    </div>
  );
};
